/**
 * AYLA na wakacjach — PetAssist gift automation.
 *
 * When the cart's eligible subtotal (all items except the gift itself)
 * reaches the campaign threshold (99 zł), a free digital product
 * ("PetAssist – 3 miesiące dostępu") is added to the cart automatically.
 * When the subtotal drops below the threshold, the gift is removed.
 *
 * Works on every page: a fetch interceptor watches Shopify cart AJAX
 * endpoints (/cart/add.js, /cart/change.js, /cart/update.js), so the
 * drawer, the cart page, and any future code all trigger a re-sync.
 *
 * Config comes from window.AYLA_WAKACJE_GIFT (set in layout/theme.liquid):
 *   { enabled, variantId, threshold (cents), currency }
 */
(function () {
  'use strict';

  var CFG = window.AYLA_WAKACJE_GIFT;
  if (!CFG || !CFG.enabled || !CFG.variantId) return;

  var THRESHOLD = CFG.threshold || 9900;
  var CURRENCY = CFG.currency || 'PLN';
  var VARIANT_ID = Number(CFG.variantId);

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var syncing = false;
  var pendingResync = false;

  function isGiftItem(item) {
    return (
      Number(item.variant_id) === VARIANT_ID ||
      (item.properties && item.properties._wakacje_gift)
    );
  }

  function eligibleSubtotal(cart) {
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (isGiftItem(item)) return;
      total += item.original_line_price || item.line_price || item.price * item.quantity;
    });
    return total;
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2,
    })
      .format(cents / 100)
      .replace('PLN', 'zł');
  }

  function cartRequest(path, body) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  async function fetchCart() {
    var response = await fetch(root + 'cart.js', { credentials: 'same-origin' });
    return response.json();
  }

  /**
   * Core reconciliation: add or remove the gift based on the eligible subtotal.
   * Returns true when the cart was mutated (callers should refresh their UI).
   */
  async function sync(cart) {
    if (syncing) {
      pendingResync = true;
      return false;
    }
    syncing = true;

    try {
      if (!cart) cart = await fetchCart();

      // Campaign is PLN-only; never touch carts in other currencies.
      if ((cart.currency || CURRENCY) !== CURRENCY) return false;

      var giftLine = (cart.items || []).find(isGiftItem);
      var subtotal = eligibleSubtotal(cart);
      var qualifies = subtotal >= THRESHOLD;
      var mutated = false;

      if (qualifies && !giftLine) {
        // Back off after a 429 so repeated page loads don't keep the throttle warm.
        var backoffUntil = Number(sessionStorage.getItem('wakacje_gift_backoff') || 0);
        if (Date.now() < backoffUntil) return false;
        var addResponse = await cartRequest('cart/add.js', {
          id: VARIANT_ID,
          quantity: 1,
          properties: { _wakacje_gift: '1' },
        });
        mutated = addResponse.ok;
        if (!addResponse.ok) {
          console.warn('[WakacjeGift] Could not add gift product', await addResponse.text());
          if (addResponse.status === 422) {
            // Variant unavailable (unpublished / other market): disable for this page.
            CFG.enabled = false;
          } else if (addResponse.status === 429) {
            sessionStorage.setItem('wakacje_gift_backoff', String(Date.now() + 5 * 60 * 1000));
          }
        }
      } else if (!qualifies && giftLine) {
        var removeResponse = await cartRequest('cart/change.js', {
          id: giftLine.key || giftLine.id,
          quantity: 0,
        });
        mutated = removeResponse.ok;
      } else if (giftLine && giftLine.quantity > 1) {
        // Someone bumped the quantity (e.g. on the cart page) — pin it back to 1.
        var pinResponse = await cartRequest('cart/change.js', {
          id: giftLine.key || giftLine.id,
          quantity: 1,
        });
        mutated = pinResponse.ok;
      }

      updateNotice(await fetchCart());

      if (mutated) {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        refreshCartPage();
      }
      return mutated;
    } catch (error) {
      console.error('[WakacjeGift] sync failed', error);
      return false;
    } finally {
      syncing = false;
      if (pendingResync) {
        pendingResync = false;
        sync();
      }
    }
  }

  /** Refresh the enhanced cart page section so the gift line appears/disappears. */
  function refreshCartPage() {
    var cartRoot = document.querySelector('.enhanced-cart');
    if (!cartRoot) return;
    if (window.EnhancedCart) {
      var instance = new window.EnhancedCart();
      if (instance && typeof instance.refreshCartSection === 'function') {
        instance.refreshCartSection();
      }
    }
  }

  /**
   * Drawer notice: progress toward the gift, or a success bar once added.
   * Injected after the free-shipping notice; same visual language.
   */
  function updateNotice(cart) {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    var anchor = drawer.querySelector('.cart-drawer__shipping-notice');
    if (!anchor) return;

    var notice = drawer.querySelector('.wakacje-gift-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'wakacje-gift-notice';
      anchor.insertAdjacentElement('afterend', notice);
    }

    if ((cart.currency || CURRENCY) !== CURRENCY || cart.item_count === 0) {
      notice.style.display = 'none';
      return;
    }
    notice.style.display = '';

    var subtotal = eligibleSubtotal(cart);
    var remaining = Math.max(0, THRESHOLD - subtotal);

    var giftName = CFG.giftName || '3 miesiące PetAssist';
    var giftNameCapitalized = giftName.charAt(0).toUpperCase() + giftName.slice(1);
    var esc = function (s) {
      return String(s).replace(/[&<>"']/g, function (ch) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
      });
    };

    if (remaining > 0) {
      notice.classList.remove('wakacje-gift-notice--achieved');
      notice.innerHTML =
        '<span class="wakacje-gift-notice__icon" aria-hidden="true">🎁</span>' +
        '<span class="wakacje-gift-notice__text">By otrzymać <strong>dostęp do PetAssist</strong> — domów jeszcze za ' +
        '<strong>' + formatMoney(remaining) + '</strong></span>';
    } else {
      notice.classList.add('wakacje-gift-notice--achieved');
      notice.innerHTML =
        '<span class="wakacje-gift-notice__icon" aria-hidden="true">🎁</span>' +
        '<span class="wakacje-gift-notice__text"><strong>' + esc(giftNameCapitalized) + '</strong> w koszyku!</span>';
    }
  }

  /** Watch all cart mutations made anywhere in the theme. */
  function interceptFetch() {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
      var result = originalFetch.apply(this, arguments);

      if (!syncing && /\/cart\/(add|change|update|clear)(\.js)?([?#]|$)/.test(url)) {
        result.then(function (response) {
          if (response.ok) sync();
        }).catch(function () {});
      }
      return result;
    };
  }

  interceptFetch();
  document.addEventListener('DOMContentLoaded', function () {
    sync();
  });
})();
